import React, { useState, useMemo } from "react";
import { PieChart, Pie, Sector, Cell, Label, Legend } from "recharts";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core";
import { useEffectAsync } from "./reactHelper";
import { userDeviceStateActions, showLoaderStateActions } from "./store";
import RefreshIcon from "@material-ui/icons/Refresh";
import Fab from "@material-ui/core/Fab";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import Typography from "@material-ui/core/Typography";

import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

const COLORS = [
  "#FFBB28",
  "#393535de",
  "#00C49F",
  "#E82C38",
  "#0088FE",
  "#A089F1",
  "#F16291",
];
const RADIAN = Math.PI / 180;
const useStyles = makeStyles((theme) => ({
  fabButtons: {
    width: "48px",
    height: "30px",
    position: "absolute",
    right: "1%",
    top: "10%",
  },
  reloadButton: {
    position: "absolute",
    top: "4%",
    right: "3%",
    zIndex: 9999,
  },
  fabButtonsSecond: {
    maxHeight: "24px",
    minHeight: "24px",
    minWidth: "24px",
    maxWidth: "24px",
    fontSize: "1rem",
  },
}));

const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  startAngle,
  endAngle,
  fill,
  percent,
  index,
  name,
  count,
}) => {
  const RADIAN = Math.PI / 180;
  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);
  const sx = cx + (outerRadius + 10) * cos;
  const sy = cy + (outerRadius + 10) * sin;
  const mx = cx + (outerRadius + 30) * cos;
  const my = cy + (outerRadius + 30) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * 22;
  const ey = my;
  const textAnchor = cos >= 0 ? "start" : "end";

  const ResetButton = ({ onBtnClick }) => {
    const classes = useStyles();
    return (
      <div className={classes.fabButtons}>
        <Fab
          size="small"
          color="primary"
          aria-label="reset"
          onClick={onBtnClick}
        >
          <RefreshIcon />
        </Fab>
      </div>
    );
  };

  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius + 6}
        outerRadius={outerRadius + 10}
        fill={fill}
      />
      <path
        d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
        stroke={fill}
        fill="none"
      />
      <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
      <text
        x={ex + (cos >= 0 ? 1 : -1) * 12}
        y={ey}
        textAnchor={textAnchor}
        fill="#333"
        fontWeight="400"
      >{`${name}: ${count}`}</text>
    </g>
  );
};

const PieChartView = ({ updateTimestamp, pieSliceClicked }) => {
  const [count, setCount] = useState(0);
  const [data, setData] = useState([]);
  const [isAccrodian, setIsAccordian] = useState(true);
  const [vendor, setVendor] = useState({});
  // const [sendRequest, setSendRequest] = useState(true);

  const dispatch = useDispatch();
  const classes = useStyles();

  let user = useSelector((state) => state.session.user);

  useEffectAsync(async () => {
    reloadData();
    const interval = setInterval(async () => {
      !document.hidden && reloadData();
    }, 25000);

    return () => clearInterval(interval);
  }, []);

  const reloadData = async () => {
    const response = await fetch(
      `/api/users/${user.id}/userDevicesState?pieChartOnly=true`
    );
    const userDeviceState = await response.json();
    setVendor(userDeviceState.serviceProvider);
    const count = userDeviceState.totalDevices.count;
    const pieData = [
      {
        name: "Idle",
        sliceKey: "idle",
        count: userDeviceState.idle.count,
        value: 100,
      },
      {
        name: "No Data",
        sliceKey: "noData",
        count: userDeviceState.noData.count,
        value: 100,
      },
      {
        name: "Running",
        sliceKey: "running",
        count: userDeviceState.running.count,
        value: 100,
      },
      {
        name: "Stopped",
        sliceKey: "stopped",
        count: userDeviceState.stopped.count,
        value: 100,
      },
      {
        name: "Inactive",
        sliceKey: "inactive",
        count: userDeviceState.inactive.count,
        value: 100,
      },
      {
        name: "Expired",
        sliceKey: "expired",
        count: userDeviceState.expired.count,
        value: 100,
      },
      {
        name: "Exp. Soon",
        sliceKey: "expiringSoon",
        count: userDeviceState.expiringSoon.count,
        value: 100,
      },
    ];
    setCount(count);
    setData(pieData);
    dispatch(userDeviceStateActions.addState(userDeviceState));
  };

  const toggleAccordian = (e, expand) => {
    setIsAccordian(expand);
  };

  const pieComp = useMemo(() => {
    return (
      <div>
        <Accordion onChange={toggleAccordian} defaultExpanded={true}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <Typography 
              className={classes.heading}
              style={{ fontWeight: 700, fontSize: 25 }}
            >
              {vendor ? vendor.name : (isAccrodian ? "Hide Chart" : "Show Chart")}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <PieChart
              width={470}
              height={280}
              style={{
                margin: 15,
                borderRadius: "10px",
                boxShadow: "4px 4px 4px 4px #dbddef",
              }}
            >
              <Pie
                data={data}
                cx={220}
                cy={128}
                labelLine={false}
                label={renderCustomizedLabel}
                outerRadius={80}
                innerRadius={55}
                fill="#8884d8"
                dataKey="value"
                onClick={pieSliceClicked}
                animationEasing="ease-out"
                paddingAngle={5}
              >
                <Label
                  value={`Total: ${count}`}
                  position="center"
                  style={{ color: "black", fontSize: 14, fontWeight: "500" }}
                />
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
            </PieChart>
          </AccordionDetails>
        </Accordion>
      </div>
    );
  }, [data, isAccrodian]);
  return pieComp;
};
export default PieChartView;
